/**
 * Intake 응답을 도메인 값으로 옮기는 공용 파서 조각.
 *
 * 서버 응답은 외부 입력으로 취급한다 — 필요한 필드가 하나라도 계약과 다르면 성공으로 보지
 * 않는다(`authSessionStorage.parseAuthSession` 과 같은 형태). 어느 모듈의 경계든 같은 조각을
 * 쓰므로 shared 가 갖는다 — 모듈끼리는 서로의 내부를 import 하지 않는다(규칙 §3).
 *
 * Owns:
 * - 모양 확인 헬퍼와 계약 위반 예외.
 *
 * Does not own:
 * - 리소스별 필드 구성 — 각 모듈의 `api/*.ts`.
 */
import { parseUtcMs } from "@/shared/utils/datetime/public";

export class IntakeContractError extends Error {
  constructor(resource: string, field: string) {
    super(`Unexpected ${resource} response shape: ${field}.`);
    this.name = "IntakeContractError";
  }
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

export function readRecord(
  value: unknown,
  resource: string,
  field: string,
): Record<string, unknown> {
  if (!isRecord(value)) {
    throw new IntakeContractError(resource, field);
  }
  return value;
}

export function readString(value: unknown, resource: string, field: string): string {
  if (typeof value !== "string" || value.trim() === "") {
    throw new IntakeContractError(resource, field);
  }
  return value;
}

export function readNullableString(value: unknown, resource: string, field: string): string | null {
  if (value === null) {
    return null;
  }
  return readString(value, resource, field);
}

export function readInteger(value: unknown, resource: string, field: string): number {
  if (typeof value !== "number" || !Number.isInteger(value)) {
    throw new IntakeContractError(resource, field);
  }
  return value;
}

/** `null` 은 "값이 없다" 이고 0 과 다르다 — 폴더 크기가 대표적인 경우다. */
export function readNullableInteger(
  value: unknown,
  resource: string,
  field: string,
): number | null {
  if (value === null) {
    return null;
  }
  return readInteger(value, resource, field);
}

export function readFiniteNumber(value: unknown, resource: string, field: string): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new IntakeContractError(resource, field);
  }
  return value;
}

/** 구간처럼 미확인일 수 있는 숫자 — `null` 은 미확인, 필드 부재는 계약 위반이다. */
export function readNullableFiniteNumber(
  value: unknown,
  resource: string,
  field: string,
): number | null {
  if (value === null) {
    return null;
  }
  return readFiniteNumber(value, resource, field);
}

/** 목록 — 항목의 모양은 호출자가 본다. */
export function readList(value: unknown, resource: string, field: string): unknown[] {
  if (!Array.isArray(value)) {
    throw new IntakeContractError(resource, field);
  }
  return value as unknown[];
}

export function readBoolean(value: unknown, resource: string, field: string): boolean {
  if (typeof value !== "boolean") {
    throw new IntakeContractError(resource, field);
  }
  return value;
}

export function readBoundedString<T extends string>(
  value: unknown,
  allowed: readonly T[],
  resource: string,
  field: string,
): T {
  const text = readString(value, resource, field);
  const match = allowed.find((candidate) => candidate === text);
  if (match === undefined) {
    throw new IntakeContractError(resource, field);
  }
  return match;
}

/** 서버 시각은 항상 있는 값이다 — 없거나 해석되지 않으면 계약 위반이다. */
export function readInstantMs(value: unknown, resource: string, field: string): number {
  const ms = parseUtcMs(typeof value === "string" ? value : null);
  if (ms === null) {
    throw new IntakeContractError(resource, field);
  }
  return ms;
}

/** `null` 과 값이 서로 다른 뜻을 갖는 시각(예: `hidden_at`). */
export function readNullableInstantMs(
  value: unknown,
  resource: string,
  field: string,
): number | null {
  if (value === null) {
    return null;
  }
  return readInstantMs(value, resource, field);
}
